import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import { classList, sectionList, subjectList } from "../../utils";
import { server } from "../../server";

/**
 * HomeworkReports
 *
 * Mirrors StudentReports / FinanceReports layout exactly:
 *  Left col  → Hero + Dataset selector + Filters + Live Snapshot
 *  Right col → Report panel (category tabs + report buttons + quick action cards)
 *
 * Datasets:
 *  1. All Homework       – every homework record
 *  2. Today's Homework   – submitted today
 *  3. With File          – homework that has an attached file
 *  4. No File            – homework without attachment
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  all: { label: "All Homework", color: "success" },
  today: { label: "Today's Homework", color: "warning" },
  withfile: { label: "With File", color: "info" },
  nofile: { label: "No File", color: "danger" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "general", label: "General" },
  { key: "classwise", label: "Class Wise" },
  { key: "teacher", label: "Teacher Wise" },
  { key: "subject", label: "Subject Wise" },
  { key: "analytics", label: "Analytics" },
];

// ─────────────────────────────────────────────────────────────────────────────
function HomeworkReports() {
  // ── raw data ───────────────────────────────────────────────────────────────
  const [allHomework, setAllHomework] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchHomework = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${server}/all_homework_of_currentDate_or_byDate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({}),
          },
        );
        const json = await res.json();
        const data = json?.["body-json"]?.body?.data;
        if (Array.isArray(data)) {
          setAllHomework(
            data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchHomework();
  }, []);

  // ── derived datasets ───────────────────────────────────────────────────────
  const todayHomework = useMemo(
    () => allHomework.filter((h) => h.createdAt?.slice(0, 10) === today),
    [allHomework],
  );
  const withFileHomework = useMemo(
    () => allHomework.filter((h) => h.file && h.file.trim() !== ""),
    [allHomework],
  );
  const noFileHomework = useMemo(
    () => allHomework.filter((h) => !h.file || h.file.trim() === ""),
    [allHomework],
  );

  const datasetMap = {
    all: { data: allHomework, loading },
    today: { data: todayHomework, loading: false },
    withfile: { data: withFileHomework, loading: false },
    nofile: { data: noFileHomework, loading: false },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── dynamic filter options from data ──────────────────────────────────────
  const teacherOptions = useMemo(() => {
    const set = new Set(
      allHomework.map((h) => h.staffFullName).filter(Boolean),
    );
    return Array.from(set).map((t) => ({ value: t, label: t }));
  }, [allHomework]);

  const classOptions = classList.map((c) => ({ value: c, label: c }));
  const sectionOptions = sectionList.map((s) => ({ value: s, label: s }));
  const subjectOptions = subjectList.map((s) => ({ value: s, label: s }));

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedClass)
      data = data.filter((h) => h.studentClass === selectedClass);
    if (selectedSection)
      data = data.filter((h) => h.section === selectedSection);
    if (selectedSubject)
      data = data.filter((h) => h.subject === selectedSubject);
    if (selectedTeacher)
      data = data.filter((h) => h.staffFullName === selectedTeacher);

    if (fromDate || toDate) {
      data = data.filter((h) => {
        const d = h.createdAt?.slice(0, 10);
        if (!d) return false;
        if (fromDate && d < fromDate) return false;
        if (toDate && d > toDate) return false;
        return true;
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((h) =>
        flatten(h).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [
    currentRaw,
    selectedClass,
    selectedSection,
    selectedSubject,
    selectedTeacher,
    fromDate,
    toDate,
    searchTerm,
  ]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const total = filteredData.length;
    const withFile = filteredData.filter(
      (h) => h.file && h.file.trim() !== "",
    ).length;
    const noFile = total - withFile;
    const todayCount = filteredData.filter(
      (h) => h.createdAt?.slice(0, 10) === today,
    ).length;

    const teacherCounts = {};
    filteredData.forEach((h) => {
      const t = h.staffFullName || "Unknown";
      teacherCounts[t] = (teacherCounts[t] || 0) + 1;
    });
    const topTeacher = Object.entries(teacherCounts).sort(
      (a, b) => b[1] - a[1],
    )[0];

    const subjectCounts = {};
    filteredData.forEach((h) => {
      const s = h.subject || "Unknown";
      subjectCounts[s] = (subjectCounts[s] || 0) + 1;
    });
    const topSubject = Object.entries(subjectCounts).sort(
      (a, b) => b[1] - a[1],
    )[0];

    return { total, withFile, noFile, todayCount, topTeacher, topSubject };
  }, [filteredData]);

  // ── select styles (identical to all report pages) ─────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTER
  // ─────────────────────────────────────────────────────────────────────────
  const formatRow = (h) => ({
    "HOMEWORK ID": toUpper(h.homeworkId || ""),
    "TEACHER ID": toUpper(h.userId || ""),
    "TEACHER NAME": toUpper(h.staffFullName || ""),
    CLASS: toUpper(h.studentClass || ""),
    SECTION: toUpper(h.section || ""),
    SUBJECT: toUpper(h.subject || ""),
    DESCRIPTION: h.description || "",
    "HAS FILE": h.file && h.file.trim() !== "" ? "YES" : "NO",
    "FILE URL": h.file || "",
    "CREATED DATE": h.createdAt
      ? new Date(h.createdAt).toLocaleDateString("en-GB")
      : "",
    "CREATED TIME": h.createdAt
      ? new Date(h.createdAt).toLocaleTimeString("en-GB")
      : "",
  });

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS
  // ─────────────────────────────────────────────────────────────────────────

  // ── General ───────────────────────────────────────────────────────────────
  const reportAll = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filteredData.map(formatRow)),
      "ALL HOMEWORK",
    );
    writeWorkbook(wb, `Homework_All_${today}.xlsx`);
  };

  const reportToday = () => {
    const list = allHomework.filter((h) => h.createdAt?.slice(0, 10) === today);
    if (!list.length) {
      toast.error("No homework submitted today");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatRow)),
      "TODAY",
    );
    writeWorkbook(wb, `Homework_Today_${today}.xlsx`);
  };

  const reportWithFile = () => {
    const list = filteredData.filter((h) => h.file && h.file.trim() !== "");
    if (!list.length) {
      toast.error("No homework with files found");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatRow)),
      "WITH FILE",
    );
    writeWorkbook(wb, `Homework_WithFile_${today}.xlsx`);
  };

  const reportNoFile = () => {
    const list = filteredData.filter((h) => !h.file || h.file.trim() === "");
    if (!list.length) {
      toast.error("No homework without files found");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(list.map(formatRow)),
      "NO FILE",
    );
    writeWorkbook(wb, `Homework_NoFile_${today}.xlsx`);
  };

  const reportDateRange = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From and To dates");
      return;
    }
    if (!filteredData.length) {
      toast.error("No records in this date range");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = filteredData
      .map(formatRow)
      .sort((a, b) =>
        (a["CREATED DATE"] || "").localeCompare(b["CREATED DATE"] || ""),
      );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "DATE RANGE",
    );
    writeWorkbook(wb, `Homework_${fromDate}_to_${toDate}.xlsx`);
  };

  // ── Class Wise ────────────────────────────────────────────────────────────
  const reportClassWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((h) => {
      const cls = h.studentClass || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatRow(h));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Homework_ClassWise_${today}.xlsx`);
  };

  const reportClassSectionWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const classGroups = {};
    filteredData.forEach((h) => {
      const cls = h.studentClass || "UNKNOWN";
      const sec = h.section || "UNKNOWN";
      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];
      classGroups[cls][sec].push(formatRow(h));
    });
    Object.entries(classGroups).forEach(([cls, sections]) => {
      const combined = [];
      Object.keys(sections)
        .sort()
        .forEach((sec) => {
          combined.push({ SECTION: `SECTION ${sec}` });
          combined.push(...sections[sec]);
          combined.push({});
        });
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(combined),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Homework_ClassSectionWise_${today}.xlsx`);
  };

  // ── Teacher Wise ──────────────────────────────────────────────────────────
  const reportTeacherWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((h) => {
      const t = h.staffFullName || h.userId || "UNKNOWN";
      if (!groups[t]) groups[t] = [];
      groups[t].push(formatRow(h));
    });
    Object.entries(groups).forEach(([t, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(t).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Homework_TeacherWise_${today}.xlsx`);
  };

  const reportTeacherSummary = () => {
    if (!allHomework.length) {
      toast.error("No data");
      return;
    }
    const counts = {};
    filteredData.forEach((h) => {
      const t = h.staffFullName || "Unknown";
      if (!counts[t])
        counts[t] = { Teacher: t, Total: 0, Today: 0, "With File": 0 };
      counts[t].Total++;
      if (h.createdAt?.slice(0, 10) === today) counts[t].Today++;
      if (h.file && h.file.trim() !== "") counts[t]["With File"]++;
    });
    const rows = Object.values(counts).sort((a, b) => b.Total - a.Total);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Teacher Summary",
    );
    writeWorkbook(wb, `Homework_TeacherSummary_${today}.xlsx`);
  };

  const reportTopTeachers = () => {
    if (!filteredData.length) {
      toast.error("No data");
      return;
    }
    const counts = {};
    filteredData.forEach((h) => {
      const t = h.staffFullName || "Unknown";
      counts[t] = (counts[t] || 0) + 1;
    });
    const rows = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t, c]) => ({ "TEACHER NAME": toUpper(t), "HOMEWORK COUNT": c }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Top Teachers",
    );
    writeWorkbook(wb, `Homework_TopTeachers_${today}.xlsx`);
  };

  // ── Subject Wise ──────────────────────────────────────────────────────────
  const reportSubjectWise = () => {
    if (!filteredData.length) {
      toast.error("No records to export");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    filteredData.forEach((h) => {
      const sub = h.subject || "UNKNOWN";
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(formatRow(h));
    });
    Object.entries(groups).forEach(([sub, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(sub).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Homework_SubjectWise_${today}.xlsx`);
  };

  const reportSubjectSummary = () => {
    if (!filteredData.length) {
      toast.error("No data");
      return;
    }
    const counts = {};
    filteredData.forEach((h) => {
      const s = h.subject || "Unknown";
      if (!counts[s]) counts[s] = { Subject: s, Total: 0, "With File": 0 };
      counts[s].Total++;
      if (h.file && h.file.trim() !== "") counts[s]["With File"]++;
    });
    const rows = Object.values(counts).sort((a, b) => b.Total - a.Total);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Subject Summary",
    );
    writeWorkbook(wb, `Homework_SubjectSummary_${today}.xlsx`);
  };

  // ── Analytics ─────────────────────────────────────────────────────────────
  const reportOverallSummary = () => {
    if (!allHomework.length) {
      toast.error("No data");
      return;
    }
    const classGroups = {};
    filteredData.forEach((h) => {
      const cls = h.studentClass || "UNKNOWN";
      if (!classGroups[cls])
        classGroups[cls] = {
          Class: cls,
          Total: 0,
          Today: 0,
          "With File": 0,
          "No File": 0,
        };
      classGroups[cls].Total++;
      if (h.createdAt?.slice(0, 10) === today) classGroups[cls].Today++;
      if (h.file && h.file.trim() !== "") classGroups[cls]["With File"]++;
      else classGroups[cls]["No File"]++;
    });
    const rows = Object.values(classGroups);
    const totals = rows.reduce(
      (acc, r) => {
        acc.Total += r.Total;
        acc.Today += r.Today;
        acc["With File"] += r["With File"];
        acc["No File"] += r["No File"];
        return acc;
      },
      { Class: "TOTAL", Total: 0, Today: 0, "With File": 0, "No File": 0 },
    );
    rows.push(totals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Overall Summary",
    );
    writeWorkbook(wb, `Homework_OverallSummary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    if (!allHomework.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    // Sheet 1: All records
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(allHomework.map(formatRow)),
      "All Records",
    );
    // Sheet 2: Teacher summary
    const tCounts = {};
    allHomework.forEach((h) => {
      const t = h.staffFullName || "Unknown";
      tCounts[t] = (tCounts[t] || 0) + 1;
    });
    const tRows = Object.entries(tCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([t, c]) => ({ TEACHER: t, COUNT: c }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(tRows),
      "Teacher Summary",
    );
    // Sheet 3: Subject summary
    const sCounts = {};
    allHomework.forEach((h) => {
      const s = h.subject || "Unknown";
      sCounts[s] = (sCounts[s] || 0) + 1;
    });
    const sRows = Object.entries(sCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([s, c]) => ({ SUBJECT: s, COUNT: c }));
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sRows),
      "Subject Summary",
    );
    writeWorkbook(wb, `Homework_MasterReport_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // General
    { cat: "general", label: "All Homework", action: reportAll },
    { cat: "general", label: "Today's Homework", action: reportToday },
    { cat: "general", label: "With File Attached", action: reportWithFile },
    { cat: "general", label: "Without File", action: reportNoFile },
    { cat: "general", label: "Date Range", action: reportDateRange },
    // Class Wise
    { cat: "classwise", label: "Class Wise", action: reportClassWise },
    {
      cat: "classwise",
      label: "Class / Section Wise",
      action: reportClassSectionWise,
    },
    // Teacher Wise
    { cat: "teacher", label: "Teacher Wise", action: reportTeacherWise },
    { cat: "teacher", label: "Teacher Summary", action: reportTeacherSummary },
    { cat: "teacher", label: "Top 10 Teachers", action: reportTopTeachers },
    // Subject Wise
    { cat: "subject", label: "Subject Wise", action: reportSubjectWise },
    { cat: "subject", label: "Subject Summary", action: reportSubjectSummary },
    // Analytics
    {
      cat: "analytics",
      label: "Overall Summary",
      action: reportOverallSummary,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-book me-1"></i> Reports &amp;
                        Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Homework Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every homework report
                          — all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedClass("");
                        setSelectedSection("");
                        setSelectedSubject("");
                        setSelectedTeacher("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search homework ID, teacher, subject, description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class & Section */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class &amp; Section
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) => {
                              setSelectedClass(opt ? opt.value : "");
                              setSelectedSection("");
                            }}
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-bookmark-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            isDisabled={!selectedClass}
                            placeholder={
                              selectedClass
                                ? "All Sections"
                                : "Select class first"
                            }
                            value={
                              selectedSection
                                ? {
                                    value: selectedSection,
                                    label: selectedSection,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSection(opt ? opt.value : "")
                            }
                            options={sectionOptions}
                            styles={selectStyles}
                            noOptionsMessage={() => "No sections available"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject & Teacher */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Subject &amp; Teacher
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-book-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Subjects"
                            value={
                              selectedSubject
                                ? {
                                    value: selectedSubject,
                                    label: selectedSubject,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSubject(opt ? opt.value : "")
                            }
                            options={subjectOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-person-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Teachers"
                            value={
                              selectedTeacher
                                ? {
                                    value: selectedTeacher,
                                    label: selectedTeacher,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedTeacher(opt ? opt.value : "")
                            }
                            options={teacherOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Date Range
                    </p>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-event text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          From
                        </p>
                      </div>
                      <div className="col-md-6">
                        <div className="bg-white rounded-3 px-3 py-2 d-flex align-items-center">
                          <i className="bi bi-calendar-check text-secondary me-2"></i>
                          <input
                            type="date"
                            className="form-control border-0 p-0 fs-6 bg-transparent"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                        <p className="m-0 text-secondary fs-6 mt-1 ms-2 lh-1">
                          To
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut — With File vs No File */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.withFile / snapshot.total) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#ffc107"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.noFile / snapshot.total) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.total ? -(snapshot.withFile / snapshot.total) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">Total</p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {snapshot.total.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-paperclip text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              With File
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.withFile.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.withFile / snapshot.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-warning-soft rounded-4 p-4 h-100">
                            <i className="bi bi-file-earmark text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              No File
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.noFile.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.noFile / snapshot.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-calendar-check text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Today
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.todayCount.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              Submitted today
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-person-workspace text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Top Teacher
                            </p>
                            <h4
                              className="m-0 fw-bold text-dark"
                              style={{ fontSize: "14px" }}
                            >
                              {snapshot.topTeacher
                                ? snapshot.topTeacher[0].split(" ")[0]
                                : "—"}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.topTeacher
                                ? `${snapshot.topTeacher[1]} submissions`
                                : "No data"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Homework
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={reportAll}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#hwReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="hwReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="hwReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Summary */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportOverallSummary}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Class Wise Stats
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportTeacherSummary}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Summary
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Teacher Wise
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Combined Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Data in One File
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportToday}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Homework
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Today</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportTopTeachers}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Toppers
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Top 10 Teachers
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportWithFile}
                          className="p-3 bg-dark-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            With File
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Attached</p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportSubjectSummary}
                          className="p-3 bg-dark-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Subject
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">Summary</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* end right col */}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default HomeworkReports;
